import { api } from './client';
import type {
  BroadcastContentType,
  BroadcastDetail,
  BroadcastListSummary,
  BroadcastSummary,
  ConversationSummary,
  MessageSummary,
  PostPoster,
} from './types';

/** Public view of a member (FR-050: no email, phone or exact location). */
export interface GroupMemberSummary {
  id: string;
  name: string;
  photoUrl?: string;
}

/**
 * specs/003 #6: a connection, separate from any chat (`qr`: one scanned the other's code; `help`: an offer to
 * help was accepted).
 */
export interface ConnectionSummary {
  status: 'connected' | 'removed' | 'blocked';
  source: 'qr' | 'help';
  since: string;
  member: PostPoster;
}

/** specs/003 #11: an invitation to a group chat, as the invitee sees it. */
export interface ChatInvitationSummary {
  id: string;
  conversationId: string;
  name: string | null;
  memberCount: number;
  inviter: PostPoster | null;
  createdAt: string;
}

export interface CommunityGroupMembers {
  total: number;
  members: GroupMemberSummary[];
}

export interface CommunityUpdateSummary {
  id: string;
  authorId: string;
  /** The words; empty when the update is only a photo. */
  content: string;
  photoUrl?: string | null;
  createdAt: string;
  expiresAt: string;
  heartCount?: number;
  /** Whether I sent a heart. */
  hearted?: boolean;
  /** Whether I have seen it (always true for my own). */
  seen?: boolean;
}

/** One of my own updates that is still up: how many neighbors saw it and who sent a heart. */
export interface MyCommunityUpdate extends CommunityUpdateSummary {
  viewCount: number;
  hearts: { id: string; name: string; photoUrl: string | null }[];
}

/**
 * FR-018–FR-028: messaging over REST (mobile/src/services/messagingApi.ts).
 * Sending, reading and reacting go over the messaging socket instead
 * (useMessagingActions.ts).
 */
export const messagingApi = {
  /** Which of the members I have a chat with are online now (the green dot). */
  presence: () => api.get<{ online: string[] }>('/presence'),
  listConversations: () => api.get<ConversationSummary[]>('/conversations'),
  listArchived: () => api.get<ConversationSummary[]>('/conversations/archived'),
  getCommunityGroup: () => api.get<ConversationSummary>('/conversations/community-group'),
  getCommunityGroupMembers: (limit = 100, offset = 0) =>
    api.get<CommunityGroupMembers>(
      `/conversations/community-group/members?limit=${limit}&offset=${offset}`,
    ),
  /** The whole history, oldest first (the API does not page it). */
  listMessages: (conversationId: string) =>
    api.get<MessageSummary[]>(`/conversations/${encodeURIComponent(conversationId)}/messages`),
  archive: (conversationId: string) =>
    api.post<ConversationSummary>(`/conversations/${encodeURIComponent(conversationId)}/archive`),
  unarchive: (conversationId: string) =>
    api.post<ConversationSummary>(`/conversations/${encodeURIComponent(conversationId)}/unarchive`),
  /** One message (text, a photo or a place) to several neighbors; `contentType` and `askForAnswer` only when used. */
  broadcast: (
    recipientIds: string[],
    content: string,
    options: { contentType?: BroadcastContentType; askForAnswer?: boolean } = {},
  ) =>
    api.post<unknown>('/conversations/broadcasts', {
      recipientIds,
      content,
      ...(options.contentType && options.contentType !== 'text' ? { contentType: options.contentType } : {}),
      ...(options.askForAnswer ? { askForAnswer: true } : {}),
    }),
  listBroadcasts: () => api.get<BroadcastSummary[]>('/broadcasts'),
  getBroadcast: (broadcastId: string) =>
    api.get<BroadcastDetail>(`/broadcasts/${encodeURIComponent(broadcastId)}`),
  editBroadcast: (broadcastId: string, content: string) =>
    api.patch<BroadcastSummary>(`/broadcasts/${encodeURIComponent(broadcastId)}`, { content }),
  removeBroadcast: (broadcastId: string) =>
    api.delete<{ id: string }>(`/broadcasts/${encodeURIComponent(broadcastId)}`),
  answerBroadcast: (broadcastId: string, answer: 'yes' | 'no') =>
    api.post<MessageSummary>(`/broadcasts/${encodeURIComponent(broadcastId)}/answer`, { answer }),
  /** Stops (or starts again) the broadcasts from the sender of this broadcast chat. */
  setBroadcastMuted: (conversationId: string, muted: boolean) =>
    api.put<{ id: string; broadcastMuted: boolean }>(
      `/conversations/${encodeURIComponent(conversationId)}/broadcast-muted`,
      { muted },
    ),
  listBroadcastLists: () => api.get<BroadcastListSummary[]>('/broadcast-lists'),
  createBroadcastList: (name: string, memberIds: string[]) =>
    api.post<BroadcastListSummary>('/broadcast-lists', { name, memberIds }),
  removeBroadcastList: (listId: string) =>
    api.delete<{ id: string }>(`/broadcast-lists/${encodeURIComponent(listId)}`),

  getMyConnectionCode: () => api.get<{ code: string }>('/accounts/me/connection-code'),
  /**
   * Opens (creating if needed) the direct conversation with a member (the code is the member id).
   * Connecting by QR code uses `connect` instead (specs/003 #6), which opens no chat.
   */
  scanConnectionCode: (code: string) =>
    api.post<ConversationSummary>('/connections/scan', { code }),

  /** specs/003 #6: connects by a scanned or typed code, without opening a chat. */
  connect: (code: string) => api.post<ConnectionSummary>('/connections', { code }),
  listConnections: () => api.get<ConnectionSummary[]>('/connections'),
  listBlocked: () => api.get<ConnectionSummary[]>('/connections/blocked'),
  removeConnection: (memberId: string) =>
    api.delete<unknown>(`/connections/${encodeURIComponent(memberId)}`),
  blockMember: (memberId: string) =>
    api.post<unknown>(`/connections/${encodeURIComponent(memberId)}/block`),
  unblockMember: (memberId: string) =>
    api.delete<unknown>(`/connections/${encodeURIComponent(memberId)}/block`),

  /** specs/003 #11: group chats joined by invitation. */
  createCircle: (name: string, memberIds: string[]) =>
    api.post<ConversationSummary>('/circles', name ? { name, memberIds } : { memberIds }),
  inviteToCircle: (conversationId: string, memberIds: string[]) =>
    api.post<{ invited: number }>(
      `/conversations/${encodeURIComponent(conversationId)}/invitations`,
      { memberIds },
    ),
  leaveCircle: (conversationId: string) =>
    api.post<{ left: true }>(`/conversations/${encodeURIComponent(conversationId)}/leave`),
  listInvitations: () => api.get<ChatInvitationSummary[]>('/invitations'),
  acceptInvitation: (invitationId: string) =>
    api.post<ConversationSummary>(`/invitations/${encodeURIComponent(invitationId)}/accept`),
  declineInvitation: (invitationId: string) =>
    api.post<unknown>(`/invitations/${encodeURIComponent(invitationId)}/decline`),

  /** A few words and/or a photo; only what is used is sent. */
  postCommunityUpdate: (content: string, photoUrl?: string) =>
    api.post<CommunityUpdateSummary>('/community-updates', {
      ...(content ? { content } : {}),
      ...(photoUrl ? { photoUrl } : {}),
    }),
  getCommunityUpdateFeed: () => api.get<CommunityUpdateSummary[]>('/community-updates/feed'),
  getMyCommunityUpdates: () => api.get<MyCommunityUpdate[]>('/community-updates/mine'),
  /** How many neighbors will see an update from me, and for how many hours. */
  getCommunityUpdateAudience: () =>
    api.get<{ count: number; hours: number }>('/community-updates/audience'),
  removeCommunityUpdate: (updateId: string) =>
    api.delete<{ id: string }>(`/community-updates/${encodeURIComponent(updateId)}`),
  markCommunityUpdateSeen: (updateId: string) =>
    api.post<{ id: string }>(`/community-updates/${encodeURIComponent(updateId)}/seen`),
  heartCommunityUpdate: (updateId: string, hearted: boolean) =>
    api.put<CommunityUpdateSummary>(`/community-updates/${encodeURIComponent(updateId)}/heart`, {
      hearted,
    }),

  reportContact: (memberId: string, reason: string, details?: string) =>
    api.post<unknown>(`/contacts/${encodeURIComponent(memberId)}/reports`, { reason, details }),
  reportMessage: (messageId: string, reason: string, details?: string) =>
    api.post<unknown>(`/messages/${encodeURIComponent(messageId)}/reports`, { reason, details }),
};
